import { forwardRef } from 'react';
import { KText } from '@/ui-library/components/text';
import {
  TemplatePreviewModal,
  CustomDateModal,
  TimingSection,
  RescheduleReasonSection,
  ContextTemplateSection,
  AdditionalContextSection,
  InterviewDetailsSection,
} from './components';
import { useScheduleWithAiForm } from './hooks/useScheduleWithAiForm';

/**
 * Shared form content for Schedule with AI: timing, context template, interview details.
 * Used by both individual drawer and bulk modal (step 2).
 * When onScheduleStateChange is provided, form does not render footer; parent drawer
 * owns the footer and can call submit via the ref. Parent receives { canSubmit, isSubmitting } via callback.
 */
export const ScheduleWithAiFormContent = forwardRef(
  function ScheduleWithAiFormContent(props, ref) {
    const form = useScheduleWithAiForm(props, ref);

    return (
      <>
        <TimingSection
          timing={form.timing}
          setTiming={form.setTiming}
          setCustomSchedule={form.setCustomSchedule}
          scheduleDisplayInCandidateTimezone={
            form.scheduleDisplayInCandidateTimezone
          }
          candidateCurrentTimeLine={form.candidateCurrentTimeLine}
          hasCandidateTimezone={form.hasCandidateTimezone}
          missingTimezoneMessage={form.missingTimezoneMessage}
          customSchedulePastError={form.customSchedulePastError}
          timingOptions={form.scheduleTimingOptions}
          onOpenCustomDateModal={() => form.setCustomDateModalOpen(true)}
        />

        <RescheduleReasonSection
          visible={!!(form.existingSession || form.hasExistingInterview)}
          rescheduleReason={form.rescheduleReason}
          onRescheduleReasonChange={form.setRescheduleReason}
        />

        <ContextTemplateSection
          selectedTemplateIds={form.selectedTemplateIds}
          setSelectedTemplateIds={form.setSelectedTemplateIds}
          templates={form.templates}
          templateOptions={form.templateOptions}
          getAvailableOptions={form.getAvailableOptions}
          newTemplateRowRef={form.newTemplateRowRef}
          setPreviewTemplate={form.setPreviewTemplate}
          addDisabled={form.addDisabled}
          addTooltip={form.addTooltip}
        />

        <AdditionalContextSection
          additionalContextFormikRef={form.additionalContextFormikRef}
        />

        <InterviewDetailsSection
          showInterviewDetailsAsEditable={form.showInterviewDetailsAsEditable}
          isPlanLoading={props.isPlanLoading}
          displayPlan={form.displayPlan}
          interviewName={form.interviewName}
          setInterviewName={form.setInterviewName}
          planInterviewers={props.planInterviewers}
          selectedInterviewers={form.selectedInterviewers}
          setSelectedInterviewers={form.setSelectedInterviewers}
          setIsInterviewDetailsEditing={form.setIsInterviewDetailsEditing}
          interviewKind={form.interviewKind}
          setInterviewKind={form.setInterviewKind}
          durationMinutes={form.durationMinutes}
          setDurationMinutes={form.setDurationMinutes}
          location={form.location}
          setLocation={form.setLocation}
          officeId={form.officeId}
          setOfficeId={form.setOfficeId}
          offices={form.offices}
          isOfficeLoading={form.isOfficeLoading}
          calendarVisibility={form.calendarVisibility}
          setCalendarVisibility={form.setCalendarVisibility}
          availableLocations={form.availableLocations}
          noteTakerEnabled={form.noteTakerEnabled}
          setNoteTakerEnabled={form.setNoteTakerEnabled}
          officeAddressFieldRef={form.officeAddressFieldRef}
        />

        {form.candidateCountLabel && (
          <KText fontSize="sm" color="secondary.600" mb="20px">
            {form.candidateCountLabel}
          </KText>
        )}

        {form.previewTemplate && (
          <TemplatePreviewModal
            template={form.previewTemplate}
            templateId={form.previewTemplate.id}
            name={form.previewTemplate.name}
            isOpen={!!form.previewTemplate}
            onClose={() => form.setPreviewTemplate(null)}
          />
        )}
        <CustomDateModal
          isOpen={form.customDateModalOpen}
          onClose={() => form.setCustomDateModalOpen(false)}
          initialValue={form.customSchedule}
          onConfirm={(val) => form.setCustomSchedule(val)}
          defaultTimezoneKey={form.candidateTimezoneKey}
        />
      </>
    );
  }
);
